import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, MessageCircle, ArrowRight } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";
import { Navbar } from "@/components/landing/Navbar";
import { WHATSAPP_URL, PAYMENT_URL } from "@/components/landing/data";

export const OrderResult = () => {
  const [params] = useSearchParams();
  const intentId = params.get("xpay_intent_id");
  const linkRef = params.get("ref");
  const [state, setState] = useState({ status: intentId ? "CHECKING" : linkRef ? "LINK_SENT" : "INVALID", ref: linkRef, amount: null });

  useEffect(() => {
    if (!intentId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await apiClient.get(`/payment-status/${encodeURIComponent(intentId)}`);
        setState({ status: data.payment_status, ref: data.order_ref, amount: data.amount_gbp });
        if (data.payment_status === "PAID" || data.payment_status === "FAILED") return;
      } catch {}
      if (++tries < 12) timer = setTimeout(poll, 1500);
      else setState((s) => ({ ...s, status: "PENDING" }));
    };
    poll();
    return () => clearTimeout(timer);
  }, [intentId]);

  const { status, ref, amount } = state;

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F2]" data-testid="order-result-page">
      <Navbar />
      <main className="flex flex-1 items-center justify-center px-5 pb-16 pt-[132px]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="luxury-card w-full max-w-lg rounded-xl p-10 text-center"
        >
          {(status === "CHECKING" || status === "PENDING") && (
            <div data-testid="result-pending">
              <Loader2 size={44} className="mx-auto animate-spin text-[#A07E1F]" />
              <h1 className="mt-6 font-serif-display text-3xl font-semibold text-[#12294D]">
                {status === "CHECKING" ? "Confirming your payment..." : "Payment is processing"}
              </h1>
              <p className="mt-3 text-sm text-[#46586F]">
                {status === "CHECKING"
                  ? "Please wait a moment while we confirm with the payment gateway."
                  : "Your bank is still confirming. If you completed payment, your order is safe — email support@myhomeworkassist.online with your receipt and we'll confirm manually."}
              </p>
            </div>
          )}
          {status === "PAID" && (
            <div data-testid="result-success">
              <CheckCircle2 size={52} className="mx-auto text-green-600" />
              <h1 className="mt-6 font-serif-display text-3xl font-semibold text-[#12294D]">Payment confirmed.</h1>
              <p className="mt-3 text-sm leading-relaxed text-[#46586F]">
                Thank you! Your order <span className="font-semibold text-[#A07E1F]">{ref}</span>
                {amount ? ` (£${amount})` : ""} is confirmed. A subject-matched expert is being assigned right now,
                and we'll be in touch shortly with next steps.
              </p>
              <Link to="/" data-testid="result-home-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#12294D] transition-colors hover:bg-[#C9A227]">
                Back to Home <ArrowRight size={14} />
              </Link>
            </div>
          )}
          {status === "FAILED" && (
            <div data-testid="result-failed">
              <XCircle size={52} className="mx-auto text-red-500" />
              <h1 className="mt-6 font-serif-display text-3xl font-semibold text-[#12294D]">Payment didn't go through.</h1>
              <p className="mt-3 text-sm leading-relaxed text-[#46586F]">
                Don't worry — no work is lost. Your order <span className="font-semibold text-[#A07E1F]">{ref}</span> is saved.
                Try again, or message us and we'll sort it out together.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Link to="/order" data-testid="result-retry-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#12294D] transition-colors hover:bg-[#C9A227]">
                  Try Again
                </Link>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="result-whatsapp-button" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#A07E1F]/50 px-8 py-3.5 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/15">
                  <MessageCircle size={14} /> WhatsApp Us
                </a>
              </div>
            </div>
          )}
          {status === "LINK_SENT" && (
            <div data-testid="result-link-sent">
              <CheckCircle2 size={52} className="mx-auto text-[#A07E1F]" />
              <h1 className="mt-6 font-serif-display text-3xl font-semibold text-[#12294D]">Order received.</h1>
              <p className="mt-3 text-sm leading-relaxed text-[#46586F]">
                Your order <span className="font-semibold text-[#A07E1F]">{ref}</span> is saved.
                Complete your payment securely using the button below, and your expert will begin immediately.
              </p>
              <a href={PAYMENT_URL} target="_blank" rel="noopener noreferrer" data-testid="result-pay-link-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#12294D] transition-colors hover:bg-[#C9A227]">
                Pay Securely Now <ArrowRight size={14} />
              </a>
            </div>
          )}
          {status === "INVALID" && (
            <div data-testid="result-invalid">
              <XCircle size={52} className="mx-auto text-[#75849B]" />
              <h1 className="mt-6 font-serif-display text-3xl font-semibold text-[#12294D]">No payment reference found.</h1>
              <p className="mt-3 text-sm text-[#46586F]">This page needs a payment reference. Start a new order below.</p>
              <Link to="/order" data-testid="result-new-order-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#12294D] transition-colors hover:bg-[#C9A227]">
                Start an Order <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};
